// src/pages/dashboard/components/superadmin/periodFormat.ts
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { Granularity } from '../../models';

/**
 * Turns the period key the Statistics module emits (`PeriodBucketing.BucketKey`) into something a
 * person can read: `2025-11-18`, `2025-W47` or `2025-11`. Charts show the tick form on the axis and
 * the long form in the tooltip.
 *
 * Month and day names come from the dayjs locale; the words around them come from translations, so
 * the caller passes its `tDashboard` in.
 */
export type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

const WEEK_KEY = /^(\d{4})-W(\d{2})$/;

/**
 * Monday of the given ISO week. January 4th is by definition always in ISO week 1, so it anchors
 * the year — note the resulting date can land in the previous calendar year.
 */
const isoWeekStart = (isoYear: number, isoWeek: number): Dayjs => {
  const jan4 = dayjs(`${isoYear}-01-04`);
  const isoDayOfWeek = jan4.day() === 0 ? 7 : jan4.day();
  return jan4.subtract(isoDayOfWeek - 1, 'day').add((isoWeek - 1) * 7, 'day');
};

/** Short label for the chart axis, where horizontal space is scarce. */
export const formatPeriodTick = (
  period: string,
  granularity: Granularity,
  t: TranslateFn
): string => {
  if (granularity === 'week') {
    const parsed = WEEK_KEY.exec(period);
    return parsed ? t('period_week', { week: Number(parsed[2]) }) : period;
  }
  return dayjs(period).format(granularity === 'month' ? 'MMM YY' : 'D. M.');
};

/** Full label for the tooltip — for a week it also spells out which days are counted in. */
export const formatPeriodLong = (
  period: string,
  granularity: Granularity,
  t: TranslateFn
): string => {
  if (granularity === 'week') {
    const parsed = WEEK_KEY.exec(period);
    if (!parsed) return period;

    const week = Number(parsed[2]);
    const start = isoWeekStart(Number(parsed[1]), week);
    const end = start.add(6, 'day');
    // Naming the month once reads better, but only when both ends actually share it.
    const sharesMonth = start.month() === end.month();

    return t('period_week_detail', {
      week,
      year: Number(parsed[1]),
      start: start.format(sharesMonth ? 'D.' : 'D. M.'),
      end: end.format('D. M.')
    });
  }
  return dayjs(period).format(granularity === 'month' ? 'MMMM YYYY' : 'D. M. YYYY');
};
